import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, 
  MapPin, 
  Euro, 
  Home as HomeIcon, 
  Maximize, 
  Bed, 
  Bath, 
  Sofa,
  Building2,
  Loader2,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImmobileDettaglio = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    job_type: '',
    monthly_budget: '',
    available_from: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties/${id}`);
      setProperty(response.data);
    } catch (err) {
      setError('Immobile non trovato');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact-request`, {
        property_id: id,
        ...contactForm
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setShowContactForm(false);
        setSubmitSuccess(false);
        setContactForm({
          name: '',
          phone: '',
          job_type: '',
          monthly_budget: '',
          available_from: ''
        });
      }, 3000);
    } catch (err) {
      alert('Errore nell\'invio della richiesta. Riprova.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const goToNextImage = () => {
    if (property.images && property.images.length > 0) {
      setLightboxIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const goToPrevImage = () => {
    if (property.images && property.images.length > 0) {
      setLightboxIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'ArrowLeft') goToPrevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, property]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <HomeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{error}</h2>
          <Link to="/immobili">
            <Button>Torna agli annunci</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/immobili">
            <Button variant="ghost" className="group">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Torna agli annunci
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Images Gallery */}
        <div className="mb-8">
          {property.images && property.images.length > 0 ? (
            <div className="space-y-4">
              <div 
                className="relative h-96 md:h-[500px] bg-slate-100 rounded-2xl overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(selectedImage)}
              >
                <img
                  src={`${BACKEND_URL}/api/property-image/${property.id}/${property.images[selectedImage].id}`}
                  alt={property.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                    <Maximize className="w-6 h-6 text-slate-900" />
                  </div>
                </div>
              </div>
              
              {property.images.length > 1 && (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {property.images.map((image, idx) => (
                    <button
                      key={image.id || idx}
                      onClick={() => {
                        setSelectedImage(idx);
                        openLightbox(idx);
                      }}
                      className={`relative h-20 rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                        selectedImage === idx 
                          ? 'border-emerald-500 scale-105' 
                          : 'border-slate-200 hover:border-emerald-300'
                      }`}
                    >
                      <img
                        src={`${BACKEND_URL}/api/property-image/${property.id}/${image.id}`}
                        alt={`Foto ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="h-96 bg-slate-100 rounded-2xl flex items-center justify-center">
              <HomeIcon className="w-20 h-20 text-slate-300" />
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and Location */}
            <div>
              <div className="flex items-start justify-between mb-3">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                  {property.title}
                </h1>
                {property.furnished && (
                  <span className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                    <Sofa className="w-4 h-4" />
                    Arredato
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2 text-lg text-slate-600">
                <MapPin className="w-5 h-5" />
                <span>{property.zone}</span>
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4">
              {property.property_type && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <HomeIcon className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">{property.property_type}</span>
                </div>
              )}
              
              {property.sqm && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <Maximize className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">{property.sqm} mq</span>
                </div>
              )}
              
              {property.rooms && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <Bed className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">{property.rooms} camere</span>
                </div>
              )}
              
              {property.bathrooms && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <Bath className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">{property.bathrooms} bagni</span>
                </div>
              )}
              
              {property.floor && (
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">Piano {property.floor}</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Descrizione</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {property.description}
              </p>
            </div>
          </div>

          {/* Sidebar - Price and Contact */}
          <div className="lg:col-span-1">
            <Card className="p-6 border-slate-200 sticky top-24">
              <div className="mb-6">
                <p className="text-slate-600 text-sm mb-1">Canone mensile</p>
                <div className="flex items-center gap-1">
                  <Euro className="w-7 h-7 text-emerald-600" />
                  <span className="text-4xl font-bold text-slate-900">
                    {property.monthly_rent}
                  </span>
                  <span className="text-slate-600">/mese</span>
                </div>
              </div>

              <Button
                onClick={() => setShowContactForm(true)}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-6 text-lg"
                size="lg"
              >
                Contattaci
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Riceverai una risposta entro 24 ore
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <Card className="w-full max-w-lg bg-white p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>

            {submitSuccess ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Richiesta Inviata!
                </h3>
                <p className="text-slate-600">
                  Ti contatteremo presto per fissare una visita
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Richiedi informazioni
                </h3>
                <p className="text-slate-600 mb-6">
                  Compila il form per essere ricontattato
                </p>

                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome e Cognome *</Label>
                    <Input
                      id="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Telefono *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="job_type">Tipo di Lavoro *</Label>
                    <Select 
                      required
                      value={contactForm.job_type}
                      onValueChange={(value) => setContactForm({...contactForm, job_type: value})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dipendente">Dipendente</SelectItem>
                        <SelectItem value="studente">Studente</SelectItem>
                        <SelectItem value="autonomo">Autonomo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="budget">Budget Mensile (€) *</Label>
                    <Input
                      id="budget"
                      type="number"
                      required
                      value={contactForm.monthly_budget}
                      onChange={(e) => setContactForm({...contactForm, monthly_budget: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="available">Disponibile da *</Label>
                    <Input
                      id="available"
                      type="date"
                      required
                      value={contactForm.available_from}
                      onChange={(e) => setContactForm({...contactForm, available_from: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 py-6"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      'Invia Richiesta'
                    )}
                  </Button>
                </form>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Lightbox Fullscreen Modal */}
      {lightboxOpen && property.images && property.images.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-8 h-8 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium">
            {lightboxIndex + 1}/{property.images.length}
          </div>

          {/* Previous Button */}
          {property.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevImage();
              }}
              className="absolute left-4 z-50 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors"
              aria-label="Immagine precedente"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Image */}
          <div 
            className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${BACKEND_URL}/api/property-image/${property.id}/${property.images[lightboxIndex].id}`}
              alt={`${property.title} - Foto ${lightboxIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
              }}
            />
          </div>

          {/* Next Button */}
          {property.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
              className="absolute right-4 z-50 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors"
              aria-label="Immagine successiva"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Thumbnail Strip */}
          {property.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-black/50 p-3 rounded-lg max-w-[90vw] overflow-x-auto">
              {property.images.map((image, idx) => (
                <button
                  key={image.id || idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(idx);
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    lightboxIndex === idx 
                      ? 'border-emerald-400 scale-110' 
                      : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <img
                    src={`${BACKEND_URL}/api/property-image/${property.id}/${image.id}`}
                    alt={`Miniatura ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImmobileDettaglio;
